<script setup>
// RM-427: the application's favorite-restaurants search, moved unchanged out
// of loginJoinDialog so the sign-up form and the Admin preview share it.
// v-model is the list of picked restaurants ({ _id, name, market, ... }).
import { computed, nextTick, ref } from 'vue'
import { storeToRefs } from 'pinia'
import lodash from 'underscore'
import { useRestaurantsStore } from '@/stores'
import noImage from '@/assets/images/ui/no_restaurant_image_placeholder-360px.jpg'
import { getRestaurantImageUrl as getRestaurantImageUrlShared } from '@core/utils/restaurantImage'

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  label: { type: String, default: 'Search for favorite restaurants' },
  required: { type: Boolean, default: true },
  errorMessages: { type: [Array, String], default: () => [] },
})

const emit = defineEmits(['update:modelValue'])

const restaurantsStore = useRestaurantsStore()
const { restaurantSearchResults } = storeToRefs(restaurantsStore)

const favoriteSearchTerm = ref('')
const fetchingFavoriteAutocomplete = ref(false)
const favoriteComboBoxRef = ref(null)

const selected = computed(() => props.modelValue || [])
const setSelected = list => emit('update:modelValue', list)

const getRestaurantImageUrl = restaurant => getRestaurantImageUrlShared(restaurant, { mediaPath: 'small', fallback: noImage })

const fetchFavoriteAutocomplete = lodash.throttle(async input => {
  favoriteSearchTerm.value = input
  if (!input || input.length < 2) {
    favoriteSearchTerm.value = ''
    restaurantSearchResults.value = []
    fetchingFavoriteAutocomplete.value = false

    return
  }
  fetchingFavoriteAutocomplete.value = true
  try {
    await restaurantsStore.searchRestaurants(input)
  } catch (error) {
    console.error('Error searching restaurants:', error)
  } finally {
    fetchingFavoriteAutocomplete.value = false
  }
}, 400)

const filteredFavoriteSearchResults = computed(() => {
  if (!favoriteSearchTerm.value || favoriteSearchTerm.value.length < 2) return []
  if (!restaurantSearchResults.value || restaurantSearchResults.value.length === 0) return []

  return restaurantSearchResults.value
})

const fuzzyFilter = (searchString, query) => {
  if (!query) return true
  const normalize = s => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

  return normalize(searchString || '').includes(normalize(query))
}

const clearSearch = () => {
  favoriteSearchTerm.value = ''
  restaurantSearchResults.value = []
  nextTick(() => {
    const inputEl = favoriteComboBoxRef.value?.$el?.querySelector('input')
    if (inputEl) {
      inputEl.value = ''
      inputEl.blur()
    }
  })
}

const addRestaurant = restaurant => {
  if (!restaurant || typeof restaurant !== 'object' || !restaurant.name) return
  const alreadySelected = selected.value.some(r => r.name === restaurant.name && r.market === restaurant.market)
  if (!alreadySelected)
    setSelected([...selected.value, restaurant])
  clearSearch()
}

const handleFavoriteSelection = val => {
  if (!val || !Array.isArray(val)) return

  // Filter out any invalid entries (strings typed by user that aren't objects)
  setSelected(val.filter(item => item && typeof item === 'object' && item.name))
  clearSearch()
}

const handleFavoriteEnterKey = event => {
  event.preventDefault()
  if (filteredFavoriteSearchResults.value.length > 0)
    addRestaurant(filteredFavoriteSearchResults.value[0])
}

const removeFavoriteRestaurant = index => {
  const next = selected.value.slice()
  next.splice(index, 1)
  setSelected(next)
}

const rules = computed(() => (props.required ? [v => (v && v.length > 0) || 'Please add at least one favorite restaurant'] : []))
</script>

<template>
  <div>
    <VCombobox
      ref="favoriteComboBoxRef"
      :model-value="selected"
      :label="label"
      :items="favoriteSearchTerm && favoriteSearchTerm.length >= 2 ? filteredFavoriteSearchResults : []"
      :menu-props="{ density: 'compact', maxHeight: '180vh', closeOnClick: true, location: 'bottom', contentClass: 'favorite-search-menu' }"
      :hide-no-data="!(favoriteSearchTerm && favoriteSearchTerm.length >= 2)"
      :custom-filter="fuzzyFilter"
      item-title="displayNameAndMarket"
      item-value="displayNameAndMarket"
      return-object
      chips
      closable-chips
      multiple
      variant="solo-filled"
      flat
      rounded="lg"
      bg-color="grey-lighten-4"
      density="comfortable"
      class="mb-4 favorite-restaurant-search"
      :rules="rules"
      :error-messages="errorMessages"
      :loading="fetchingFavoriteAutocomplete"
      @update:model-value="handleFavoriteSelection"
      @update:search="fetchFavoriteAutocomplete"
      @keydown.enter.prevent="handleFavoriteEnterKey"
    >
      <template #chip="{ item, props: chipProps }">
        <VChip
          v-bind="chipProps"
          closable
        >
          {{ item.raw.name }} - {{ item.raw.market }}
        </VChip>
      </template>
      <template #no-data>
        <VListItem v-if="favoriteSearchTerm && favoriteSearchTerm.length >= 2 && !fetchingFavoriteAutocomplete">
          <VListItemTitle class="text-body-2 text-medium-emphasis">
            {{ favoriteSearchTerm }} is not in our database yet<br>
          </VListItemTitle>
        </VListItem>
        <VListItem v-else-if="favoriteSearchTerm && favoriteSearchTerm.length === 1">
          <VListItemTitle class="text-body-2 text-medium-emphasis">
            Type at least 2 characters to search
          </VListItemTitle>
        </VListItem>
      </template>
      <template #item="{ item }">
        <div
          class="search-dropdown-card"
          @click.stop="addRestaurant(item.raw)"
        >
          <div class="search-dropdown-content">
            <div class="search-dropdown-title-with-heart">
              <span class="search-dropdown-title-wrapper">
                <span
                  class="search-dropdown-title"
                  :class="{ 'text-preferred': item.raw.status == 'Preferred' }"
                >{{ item.raw.name }}</span>
              </span>
            </div>
            <div class="search-dropdown-subtitle-wrapper">
              <p
                v-if="item.raw.group"
                class="search-dropdown-subtitle"
              >
                {{ item.raw.group }}
              </p>
              <p
                v-if="item.raw.status == 'Preferred'"
                class="preferred-word-line"
              >
                Preferred
              </p>
              <p
                v-if="item.raw.market"
                class="search-dropdown-subtitle"
              >
                {{ item.raw.market }}
              </p>
            </div>
          </div>
          <div class="search-dropdown-image-container">
            <VImg
              :src="getRestaurantImageUrl(item.raw)"
              class="restaurant-image rounded-lg"
              cover
              height="72"
              width="72"
            />
          </div>
        </div>
      </template>
    </VCombobox>

    <!-- Selected restaurant cards -->
    <div
      v-if="selected.length > 0"
      class="selected-favorites-list mb-3"
    >
      <div
        v-for="(restaurant, index) in selected"
        :key="index"
        class="selected-favorite-card"
      >
        <div class="selected-favorite-content">
          <div class="selected-favorite-info">
            <span class="selected-favorite-name-row">
              <span
                class="selected-favorite-name"
                :class="{ 'text-preferred': restaurant.status == 'Preferred' }"
              >{{ restaurant.name }}</span>
            </span>
            <span
              v-if="restaurant.group"
              class="selected-favorite-detail"
            >{{ restaurant.group }}</span>
            <span
              v-if="restaurant.status == 'Preferred'"
              class="preferred-word-line"
            >Preferred</span>
            <span class="selected-favorite-detail">{{ restaurant.market }}</span>
          </div>
        </div>
        <div class="selected-favorite-right">
          <div class="selected-favorite-image">
            <VImg
              :src="getRestaurantImageUrl(restaurant)"
              class="rounded-lg"
              cover
              height="56"
              width="56"
            />
          </div>
          <VBtn
            size="x-small"
            variant="flat"
            color="grey-darken-2"
            class="selected-favorite-remove"
            icon="mdi-close"
            @click="removeFavoriteRestaurant(index)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.favorite-restaurant-search :deep(input) {
  font-size: 16px;
}

.favorite-restaurant-search :deep(.v-chip) {
  font-size: 14px;
  margin: 4px 2px;
}

.favorite-restaurant-search :deep(.v-field__input) {
  padding: 20px 20px 20px;
  min-height: 56px;
  gap: 6px;
}

.selected-favorites-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.selected-favorite-card {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 12px;
  border: thin solid var(--ds-border-light);
  border-radius: 12px;
  background: var(--ds-surface-card);
  gap: 8px;
}

.selected-favorite-content {
  display: flex;
  flex: 1;
  min-width: 0;
}

.selected-favorite-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  text-align: left;
}

.selected-favorite-name-row {
  display: flex;
  align-items: center;
}

.selected-favorite-name {
  font-weight: 600;
  font-size: 14px;
  line-height: 1.3;
  color: var(--ds-text-strong);
  word-wrap: break-word;
}

.text-preferred {
  color: var(--ds-brand-primary-dark) !important;
}

.selected-favorite-detail {
  font-size: 12px;
  color: var(--ds-text-muted);
  line-height: 1.3;
}

/* Preferred — plain word in brand green (no icon), matching the shared card */
.preferred-word-line {
  margin: 0;
  width: fit-content;
  font-family: var(--ds-font-body);
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1.3;
  color: var(--ds-brand-primary-dark);
}

.selected-favorite-right {
  flex-shrink: 0;
  position: relative;
  width: 56px;
  height: 56px;
}

.selected-favorite-image {
  width: 56px;
  height: 56px;
  border-radius: 8px;
  overflow: hidden;
}

.selected-favorite-remove {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px !important;
  height: 20px !important;
  min-width: 20px !important;
  z-index: 1;
}
</style>

<style lang="scss">
/* The dropdown menu is teleported out of this component, so this block is global. */
.favorite-search-menu {
  min-height: 200px;

  .v-list {
    max-height: 60vh;
    overflow-y: auto;
  }

  .v-list-item {
    min-height: 48px;
  }

  .search-dropdown-card {
    display: flex;
    align-items: flex-start;
    padding: 12px;
    gap: 10px;
    cursor: pointer;
    transition: background-color 0.2s;
    border: 1px solid var(--ds-border-default);
    border-radius: 12px;
    margin: 8px 12px;
    background: var(--ds-surface-card);
    min-height: 80px;
  }

  .search-dropdown-card:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }

  .search-dropdown-content {
    flex: 1;
    min-width: 0;
    max-width: calc(100% - 70px);
    display: flex;
    flex-direction: column;
    gap: 2px;
    overflow: hidden;
  }

  .search-dropdown-title-with-heart {
    display: flex;
    align-items: flex-start;
    gap: 2px;
    min-height: 1.4em;
  }

  .search-dropdown-icon-placeholder {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    margin-top: 7.2px;
    min-width: 20px;
    width: 20px;
  }

  .search-dropdown-title-wrapper {
    flex: 1;
    min-width: 0;
    display: inline-block;
  }

  .search-dropdown-title {
    font-weight: 500;
    font-size: 15px;
    line-height: 1.4;
    color: var(--ds-text-strong);
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
    display: inline;
  }

  .text-preferred {
    color: var(--ds-brand-primary-dark) !important;
  }

  .search-dropdown-subtitle-wrapper {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding-left: 22px;
  }

  .search-dropdown-subtitle {
    font-size: 13px;
    line-height: 1.5;
    color: var(--ds-text-muted);
    margin: 0;
  }

  .search-dropdown-image-container {
    flex-shrink: 0;
    width: 60px;
    height: 60px;
    min-width: 60px;
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px 4px 4px 0;
    box-sizing: border-box;
  }
}
</style>
